/**
 * Compositional index/list page layout components
 * Provides a consistent structure for index pages with headers, search, and content
 */

import { HTMLMotionProps, motion } from "framer-motion";
import { Search, X } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/utils";

const IndexLayout = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "@container sm:px-0 w-full max-w-7xl mx-auto flex-1 flex flex-col",
      className,
    )}
    {...props}
  />
));
IndexLayout.displayName = "IndexLayout";

const IndexLayoutStickyHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "lg:sticky lg:top-0 lg:z-50 lg:pb-2 lg:-mx-6 lg:px-6",
      "lg:[background:linear-gradient(to_bottom,hsl(var(--background)/1),hsl(var(--background)/1))]",
      "lg:backdrop-blur-sm",
      "[mask-image:linear-gradient(to_bottom,black_calc(100%-16px),transparent)]",
      className,
    )}
    {...props}
  >
    {children}
  </div>
));
IndexLayoutStickyHeader.displayName = "IndexLayoutStickyHeader";

const IndexLayoutHeader = React.forwardRef<
  HTMLDivElement,
  HTMLMotionProps<"div"> & {
    children: React.ReactNode;
  }
>(({ className, children, ...props }, ref) => {
  // Extract children by type
  let icon = null;
  let title = null;
  let description = null;
  let action = null;
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    const displayName = (child.type as any).displayName;
    if (displayName === "IndexLayoutHeaderIcon") icon = child;
    else if (displayName === "IndexLayoutHeaderTitle") title = child;
    else if (displayName === "IndexLayoutHeaderDescription")
      description = child;
    else if (displayName === "IndexLayoutHeaderAction") action = child;
  });
  return (
    <motion.div
      initial={{ opacity: 1, y: 0 }}
      ref={ref}
      className={cn(
        "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:pt-[50px] pt-4",
        className,
      )}
      {...props}
    >
      {/* Left: icon, then title and description next to it */}
      <div className="flex flex-row items-center gap-4 flex-1 min-w-0">
        {icon}
        <div className="flex flex-col min-w-0">
          {title}
          {description && <div>{description}</div>}
        </div>
      </div>
      {/* Action (right) */}
      {action}
    </motion.div>
  );
});
IndexLayoutHeader.displayName = "IndexLayoutHeader";

interface IndexLayoutHeaderIconProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactElement<{ className?: string }>;
}

const IndexLayoutHeaderIcon = React.forwardRef<
  HTMLDivElement,
  IndexLayoutHeaderIconProps
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      // Smaller icon container
      "flex items-center justify-center flex-shrink-0 mr-0 @sm:mr-0 bg-muted rounded-xl p-3",
      className,
    )}
    {...props}
  >
    {React.cloneElement(children, {
      className: cn(
        "h-6 w-6 @sm:h-9 @sm:w-9 text-muted-foreground relative top-[-1px]",
        children.props.className,
      ),
    })}
  </div>
));
IndexLayoutHeaderIcon.displayName = "IndexLayoutHeaderIcon";

const IndexLayoutHeaderTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h1
    ref={ref}
    className={cn("text-2xl @sm:text-3xl font-bold leading-tight", className)}
    {...props}
  />
));
IndexLayoutHeaderTitle.displayName = "IndexLayoutHeaderTitle";

const IndexLayoutHeaderDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm @sm:text-base text-muted-foreground", className)}
    {...props}
  />
));
IndexLayoutHeaderDescription.displayName = "IndexLayoutHeaderDescription";

const IndexLayoutHeaderAction = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center gap-2 mt-4 @sm:mt-0 @sm:w-auto",
      className,
    )}
    {...props}
  />
));
IndexLayoutHeaderAction.displayName = "IndexLayoutHeaderAction";

const IndexLayoutSearch = React.forwardRef<
  HTMLDivElement,
  HTMLMotionProps<"div"> & {
    children: React.ReactNode;
  }
>(({ className, children, ...props }, ref) => (
  <motion.div
    initial={{ opacity: 1, y: 0 }}
    ref={ref}
    className={cn("mb-6", className)}
    {...props}
  >
    {children}
  </motion.div>
));
IndexLayoutSearch.displayName = "IndexLayoutSearch";

interface IndexLayoutSearchBarProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear?: () => void;
  placeholder?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  className?: string;
}

const IndexLayoutSearchBar: React.FC<IndexLayoutSearchBarProps> = ({
  value,
  onChange,
  onClear,
  placeholder = "Search...",
  inputProps,
  className,
}) => (
  <div className={cn("relative w-full", className)}>
    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={cn(
        "w-full pl-12 h-12 text-sm bg-background/80 focus:border-primary/50 focus:ring-rust/10 transition-all duration-200 rounded-md border",
        inputProps?.className,
      )}
      {...inputProps}
    />
    {value && onClear && (
      <button
        type="button"
        className="rounded-full absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-muted/50 flex items-center justify-center"
        onClick={onClear}
        tabIndex={-1}
        aria-label="Clear search"
      >
        <X className="!w-3 !h-3" />
      </button>
    )}
  </div>
);
IndexLayoutSearchBar.displayName = "IndexLayoutSearchBar";

const IndexLayoutContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("space-y-6 @sm:space-y-8 mt-10 h-full", className)}
    {...props}
  />
));
IndexLayoutContent.displayName = "IndexLayoutContent";

const ZeroStateMotion: React.FC<
  React.PropsWithChildren<{ className?: string }>
> = ({ children, className }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3, delay: 0.2 }}
    className={cn(
      "flex flex-col items-center justify-center min-h-[400px] text-center px-4",
      className,
    )}
  >
    {children}
  </motion.div>
);

export {
  IndexLayout,
  IndexLayoutContent,
  IndexLayoutHeader,
  IndexLayoutHeaderAction,
  IndexLayoutHeaderDescription,
  IndexLayoutHeaderIcon,
  IndexLayoutHeaderTitle,
  IndexLayoutSearch,
  IndexLayoutSearchBar,
  IndexLayoutStickyHeader,
  ZeroStateMotion,
};
